import React from "react";
import { Space } from "antd";
import { AxSkeleton, AxDivider, AxButton } from "@dynata/axiom3";

class Skeleton extends React.Component {
  render() {
    return (
      <div className="section" id="skeleton">
        <h3>Skeleton</h3>
        <p>
          Provide a placeholder while you wait for content to load, or to
          visualise content that doesn't exist yet.
        </p>
        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>
        <Space direction="vertical">
          <Space size="large" direction="vertical">
            <pre>{`<AxSkeleton active paragraph={{ rows: 0 }} />`}</pre>
            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/skeleton/#API"
                >
                  https://ant.design/components/skeleton/#API
                </AxButton>
              </p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>active</td>
                  <td>Show animation effect</td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>avatar</td>
                  <td>Show avatar placeholder</td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>loading</td>
                  <td>Display the skeleton when true</td>
                  <td>boolean</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>paragraph</td>
                  <td>Show paragraph placeholder</td>
                  <td>boolean</td>
                  <td>true</td>
                </tr>
                <tr>
                  <td>round</td>
                  <td>Show paragraph and title radius when true</td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>title</td>
                  <td>Show title placeholder</td>
                  <td>boolean</td>
                  <td>true</td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/skeleton/#API"
                      >
                        {`<Skeleton />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                </tr>
              </tbody>
            </table>
            <div className="prop-header">
              <p className="props-title">SkeletonAvatarProps</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>active</td>
                  <td>
                    Show animation effect, only valid when used avatar
                    independently
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>shape</td>
                  <td>Set the shape of avatar</td>
                  <td>
                    <code>circle</code> | <code>square</code>
                  </td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>size</td>
                  <td>Set the size of avatar</td>
                  <td>
                    number | <code>large</code> | <code>small</code> |{" "}
                    <code>default</code>
                  </td>
                  <td>-</td>
                </tr>
              </tbody>
            </table>

            <div className="prop-header">
              <p className="props-title">SkeletonTitleProps</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>width</td>
                  <td>Set the width of title</td>
                  <td>number | string</td>
                  <td>-</td>
                </tr>
              </tbody>
            </table>
            <div className="prop-header">
              <p className="props-title">SkeletonParagraphProps</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>rows</td>
                  <td>Set the row count of paragraph</td>
                  <td>number</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>width</td>
                  <td>
                    Set the width of paragraph. When width is an Array, it can
                    set the width of each row. Otherwise only set the last row
                    width
                  </td>
                  <td>number | string | Array&lt;number | string&gt;</td>
                  <td>-</td>
                </tr>
              </tbody>
            </table>
            <div className="prop-header">
              <p className="props-title">SkeletonButtonProps</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>active</td>
                  <td>Show animation effect</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>block</td>
                  <td>Option to fit button width to its parent width</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td>4.17.0</td>
                </tr>
                <tr>
                  <td>shape</td>
                  <td>Set the shape of button</td>
                  <td>
                    <code>circle</code> | <code>round</code> |{" "}
                    <code>square</code> | <code>default</code>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>size</td>
                  <td>Set the size of button</td>
                  <td>
                    <code>large</code> | <code>small</code> |{" "}
                    <code>default</code>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
            <div className="prop-header">
              <p className="props-title">SkeletonInputProps</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>active</td>
                  <td>Show animation effect</td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>size</td>
                  <td>Set the size of input</td>
                  <td>
                    <code>large</code> | <code>small</code> |{" "}
                    <code>default</code>
                  </td>
                  <td>-</td>
                </tr>
              </tbody>
            </table>

            <pre>{`<AxSkeleton active paragraph={{ rows: 4 }} />`}</pre>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Default
        </AxDivider>

        <Space>
          <div style={{ width: "500px" }}>
            <AxSkeleton active paragraph={{ rows: 4 }} />
          </div>
        </Space>
      </div>
    );
  }
}
export default Skeleton;
